import { escapeHtml } from '../../utils/escapeHtml';
import { renderBarcodeSvg } from '../../utils/barcode';
import { formatDispenseQuantity } from '../../utils/formatters';
import { formatPrescriberName } from '../../utils/prescriberDisplay';
import { getPharmacyInfo } from './ThermalLabelPrint';

/**
 * Format expiry date for display: DD/MM/YYYY or MM/YYYY if no day
 * Accepts YYYY-MM-DD, YYYY-MM, or MM/YYYY formats
 */
const formatExpiryForDisplay = (expiry: string): string => {
  if (!expiry) return '';
  // Already in display format (MM/YYYY)
  if (/^\d{2}\/\d{4}$/.test(expiry)) return expiry;
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(expiry)) {
    const [year, month, day] = expiry.split('-');
    return `${day}/${month}/${year}`;
  }
  // YYYY-MM (no day)
  if (/^\d{4}-\d{2}$/.test(expiry)) {
    const [year, month] = expiry.split('-');
    return `${month}/${year}`;
  }
  return expiry;
};

export interface ThermalReceiptData {
  rxNumber: string;
  patientName: string;
  fillDate: string;
  address1: string;
  address2: string;
  dispQty: number;
  brandName: string;
  genericName: string;
  din: string;
  daysSupply: string;
  refills: string;
  /**
   * Prescriber as printed, INCLUDING the title Kroll's practitioner designation
   * dictates — "NP Salvano, Lorraine" / "RPh Chau, Kevin" / "Dr. Chow, Andrew"
   * (GP-573). Renderers must not add a title of their own.
   */
  doctorName: string;
  /**
   * Explicit prescriber title override for formatPrescriberName (release 2138
   * review deploy shim): `undefined` = backend didn't resolve one (old backend
   * — renderer falls back to the pre-existing "Dr." prefix), `''` = backend
   * said no title / already embedded in doctorName, `'NP'` etc. = prefix it.
   * TODO(remove after 2.3.517 backend is live)
   */
  prescriberTitle?: string;
  cost: number;
  fee: number;
  total: number;
  price: number; // Cash plan pays
  insurancePays: number;
  insurancePlanNames?: string; // Names of insurance plans that paid (comma-separated)
  waived: number; // Discount to fee (Rx.DiscountFee)
  pharmacyId?: string;      // Used to look up pharmacy info
  pharmacyName?: string;    // Pharmacy name
  pharmacyAddress?: string; // Pharmacy street address
  pharmacyCity?: string;    // Pharmacy city
  pharmacyProvince?: string; // Pharmacy province
  pharmacyPostalCode?: string; // Pharmacy postal code
  pharmacyPhone?: string;   // Pharmacy phone number
  patientPhone?: string;    // Patient phone number
  manufacturer?: string;    // Drug manufacturer (Mfr)
  form?: string;           // Drug form (e.g., "VIA" for vials)
  deliveryType?: string;   // GP-333: Delivery type (InHouse, Courier, 3rd_Party, etc.)
  shipmentDate?: string;   // GP-333: Shipment/delivery date (YYYY-MM-DD)
  lotNumber?: string;      // Lot number from packaging verification
  expiryDate?: string;     // Expiry date (YYYY-MM-DD or YYYY-MM)
  productVerifierName?: string; // GP-654: Full name of the user who product-checked the order
}

/**
 * Generate a 16-digit numeric barcode from Rx number
 * Format: 00000{rxNumber}0000000 (padded to 16 digits)
 */
const generateNumericBarcode = (rxNumber: string): string => {
  const numericRx = rxNumber.replace(/\D/g, '');
  return numericRx.padStart(16, '0');
};

export const printThermalReceipt = (data: ThermalReceiptData) => {
  // Generate numeric barcode for display
  const numericBarcode = generateNumericBarcode(data.rxNumber);
  // PRX-39: rendered here from the bundled jsbarcode and inlined into the
  // popup — it used to be drawn by a CDN script inside the popup, which
  // printed a barcode-less receipt whenever the script lost the race.
  const receiptBarcodeSvg = renderBarcodeSvg(numericBarcode, {
    width: 2,
    height: 35,
    displayValue: false,
    margin: 0,
  });

  // Format drug display: "{qty} {form} {brandName}"
  // GP-889: keep Kroll's dispense-qty decimals (2.4 → "2.4", not Math.round → "2").
  const drugDisplay = `${formatDispenseQuantity(data.dispQty)} ${data.form || ''} ${data.brandName}`.trim();

  // Extract just the DIN number (remove "DIN:" prefix if present)
  const dinValue = data.din.replace(/^DIN:\s*/i, '');

  // Extract just the days number (remove "Days:" prefix if present)
  const daysValue = data.daysSupply.replace(/^Days:\s*/i, '');

  // Lot/expiry no longer displayed on receipt

  // Get pharmacy info from lookup based on pharmacyId
  const pharmacyInfo = getPharmacyInfo(data.pharmacyId);

  // Format pharmacy address line (city, province postal)
  const pharmacyAddressLine2 = [
    pharmacyInfo.city,
    pharmacyInfo.province,
    pharmacyInfo.postalCode
  ].filter(Boolean).join(' ');

  const receiptHTML = `
    <div class="thermal-receipt">
      <div class="receipt-content">
        <!-- Pharmacy Header - Compact -->
        <div class="pharmacy-header">
          <div class="pharmacy-name">${pharmacyInfo.name}</div>
          <div class="pharmacy-address">${[pharmacyInfo.address, pharmacyAddressLine2].filter(Boolean).join(', ')}${pharmacyInfo.phone ? ` | ${pharmacyInfo.phone}` : ''}</div>
        </div>

        <!-- Title -->
        <div class="receipt-title">OFFICIAL PRESCRIPTION RECEIPT</div>

        <!-- Rx Info Section -->
        <div class="rx-info-section">
          <div class="rx-header">
            <div class="rx-number">Rx: ${data.rxNumber}</div>
            <div class="patient-date-block">
              <div class="patient-name">${data.patientName}</div>
              <div class="fill-date">${data.fillDate}</div>
            </div>
          </div>
          <div class="address-row">
            <div class="address-left">
              <div class="address-line">${data.address1}</div>
              <div class="address-line">${data.address2}</div>
            </div>
            ${data.patientPhone ? `<div class="phone-right">${data.patientPhone}</div>` : ''}
          </div>
          ${(data.deliveryType || data.shipmentDate) ? `
          <div class="delivery-info-row">
            ${data.deliveryType ? `<span class="delivery-type-badge">${escapeHtml(data.deliveryType)}</span>` : ''}
            ${data.shipmentDate ? `<span class="shipment-date">Ship: ${escapeHtml(data.shipmentDate)}</span>` : ''}
          </div>` : ''}
        </div>

        <!-- Medication Info -->
        <div class="medication-section">
          <div class="drug-line">${drugDisplay}</div>
          <div class="generic-name"><em>${data.genericName}</em></div>
          <div class="drug-details">
            <span>DIN: ${dinValue}</span>
            <span class="detail-spacer">Mfr: ${data.manufacturer || 'HPH'}</span>
            <span class="detail-spacer">Days: ${daysValue}</span>
            <span class="detail-spacer">Refills: ${data.refills}</span>
          </div>
          <!-- GP-573: doctorName already carries the title Kroll's practitioner
               designation dictates (NP / RPh / Dr. …) — never re-prefix "Dr.".
               formatPrescriberName only restores "Dr." for an OLD backend that
               still sends an untitled name (release 2138 review shim).
               TODO(remove after 2.3.517 backend is live) -->
          <div class="doctor-name">${formatPrescriberName(data.doctorName, data.prescriberTitle)}</div>
        </div>

        <!-- Pricing Section - Two Column Layout -->
        <div class="pricing-section">
          <div class="pricing-left">
            <div class="price-row">
              <span class="price-label">Cost:</span>
              <span class="price-value">${data.cost.toFixed(2)}</span>
            </div>
            <div class="price-row">
              <span class="price-label">Fee:</span>
              <span class="price-value">${data.fee.toFixed(2)}</span>
            </div>
            <div class="price-row total-row">
              <span class="price-label">Total:</span>
              <span class="price-value">${data.total.toFixed(2)}</span>
            </div>
          </div>
          <div class="pricing-right">
            ${(data.insurancePlanNames || '').split('|').filter(Boolean).map(plan => `
            <div class="insurance-row">${plan}</div>
            `).join('')}
            ${data.waived > 0 ? `<div class="insurance-row">Waived: $${data.waived.toFixed(2)}</div>` : ''}
          </div>
        </div>

        <!-- Patient Pays - Yellow Highlight -->
        <div class="patient-pays">
          Patient Pays: ${data.price.toFixed(2)}
        </div>

        <!-- Signature Line -->
        <div class="signature-line">
          Signature: ${data.productVerifierName ? escapeHtml(data.productVerifierName) : '________________________'}
        </div>

        <!-- Barcode Section -->
        <div class="barcode-section">
          ${receiptBarcodeSvg}
          <div class="barcode-number">${numericBarcode}</div>
          <div class="tx-number">Tx: ${data.rxNumber}</div>
        </div>

      </div>
    </div>
  `;

  // Open print window
  const printWindow = window.open('', '_blank', 'width=600,height=800');
  if (!printWindow) {
    alert('Please allow pop-ups for this site');
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print Receipt - Rx ${data.rxNumber}</title>
        <style>
          @page {
            size: 4in 4in;
            margin: 0;
          }

          @media print {
            @page {
              size: 4in 4in;
              margin: 0;
            }
            html, body {
              width: 4in;
              height: 4in;
              margin: 0;
              padding: 0;
            }
          }

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          html, body {
            margin: 0;
            padding: 0;
            width: 4in;
            height: 4in;
            overflow: hidden;
          }

          .thermal-receipt {
            width: 4in;
            height: 4in;
            padding: 0.1in 0.12in;
            background: white;
            font-family: Arial, sans-serif;
            font-size: 9pt;
            line-height: 1.15;
          }

          .receipt-content {
            width: 100%;
            height: 100%;
          }

          /* Pharmacy Header - Compact */
          .pharmacy-header {
            text-align: center;
            margin-bottom: 0.04in;
            padding-bottom: 0.02in;
            border-bottom: 1px solid #ccc;
          }

          .pharmacy-name {
            font-weight: bold;
            font-size: 10pt;
          }

          .pharmacy-address {
            font-size: 7pt;
            line-height: 1.1;
          }

          /* Title */
          .receipt-title {
            text-align: center;
            font-weight: bold;
            font-size: 11pt;
            padding: 0.04in 0;
            border-bottom: 1px solid #000;
            margin-bottom: 0.06in;
          }

          /* Rx Info Section */
          .rx-info-section {
            margin-bottom: 0.06in;
          }

          .rx-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 0.02in;
          }

          .rx-number {
            font-weight: bold;
            font-size: 11pt;
          }

          .patient-date-block {
            text-align: right;
          }

          .patient-name {
            font-weight: bold;
            font-size: 9pt;
          }

          .fill-date {
            font-size: 8pt;
          }

          .address-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }

          .address-left {
            font-size: 8pt;
          }

          .address-line {
            margin-bottom: 0.01in;
          }

          .phone-right {
            font-size: 8pt;
            text-align: right;
          }

          .delivery-info-row {
            display: flex;
            align-items: center;
            gap: 0.1in;
            margin-top: 0.03in;
            font-size: 8pt;
          }

          .delivery-type-badge {
            font-weight: bold;
            background: #000;
            color: #fff;
            padding: 1px 4px;
            font-size: 7.5pt;
          }

          .shipment-date {
            font-size: 7.5pt;
          }

          /* Medication Section */
          .medication-section {
            padding: 0.04in 0;
            border-top: 1px solid #ccc;
            border-bottom: 1px solid #ccc;
            margin-bottom: 0.04in;
          }

          .drug-line {
            font-weight: bold;
            font-size: 9pt;
            margin-bottom: 0.01in;
          }

          .generic-name {
            font-size: 8pt;
            margin-bottom: 0.02in;
          }

          .drug-details {
            font-size: 7.5pt;
            margin-bottom: 0.02in;
          }

          .detail-spacer {
            margin-left: 0.12in;
          }

          .lot-expiry-row {
            font-size: 7.5pt;
            font-weight: bold;
            margin-bottom: 0.02in;
          }

          .doctor-name {
            font-weight: bold;
            font-size: 8pt;
          }

          /* Pricing Section - Two Column */
          .pricing-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.04in;
            font-size: 8pt;
          }

          .pricing-left {
            width: 45%;
          }

          .pricing-right {
            width: 45%;
            text-align: right;
          }

          .price-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.01in;
          }

          .price-label {
            font-weight: normal;
          }

          .price-value {
            text-align: right;
          }

          .total-row {
            font-weight: bold;
            border-top: 1px solid #000;
            padding-top: 0.01in;
            margin-top: 0.01in;
          }

          .insurance-row {
            font-size: 7.5pt;
            margin-bottom: 0.01in;
            text-align: right;
          }

          /* Patient Pays - Yellow Highlight */
          .patient-pays {
            background-color: #ffff00;
            text-align: center;
            font-weight: bold;
            font-size: 11pt;
            padding: 0.05in 0.1in;
            margin: 0.04in 0;
            border: 1px solid #ccc;
          }

          /* RPhT Signature Line */
          .signature-line {
            font-size: 8pt;
            margin: 0.06in 0;
            padding-top: 0.04in;
            border-top: 1px solid #ccc;
          }

          /* Barcode Section */
          .barcode-section {
            text-align: center;
            margin: 0.04in 0;
          }

          .barcode-section svg {
            width: 3in;
            height: 0.4in;
            display: block;
            margin: 0 auto;
          }

          .barcode-number {
            font-size: 8pt;
            font-family: 'Courier New', monospace;
            margin-top: 0.02in;
            letter-spacing: 0.03in;
          }

          .tx-number {
            font-size: 7pt;
            margin-top: 0.01in;
          }

        </style>
      </head>
      <body>
        ${receiptHTML}
        <script>
          // PRX-39: the barcode is already inline SVG in the markup above —
          // nothing to load, nothing to race. Print once the document is ready.
          setTimeout(function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          }, 250);
        </script>
      </body>
    </html>
  `);

  printWindow.document.close();
};
